import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators';
import { getMarkerIcon, getNumberedPointIconColor, getNumberedPointIconShape } from '../helpers/markers';
import { isFalsy, isValidString, shouldDisplayAddPointElement } from '../helpers/utils';

import { CheckIn } from '../types';
import { PointCardBase } from './point-card-base';
import { makeColorsIntoNumberSvg } from '../helpers/colors';
import topicData from '../helpers/state/topic-data';

@customElement('grid-point-card')
export class GridPointCard extends PointCardBase {
    static amplitude_callsite: string = 'List Point Card';

    @state()
    multiSelectionMode: boolean = false;

    @state()
    columns: number = 2;

    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        await super.updated(changedProperties);

        if (changedProperties.has('columns')) {
            this.updateWidth();
        }
    }

    updateWidth() {
        const percent = 100 / this.columns;
        const buffer = 10;
        // update width on host
        this.style.width = `min(calc(${percent}% - ${buffer}px), 250px)`;
    }

    private async onCardClick() {
        this.dispatchEvent(new CustomEvent('on-card-click'));
    }

    private async onDeselectPoint() {
        if (this.point) {
            await topicData.mapComponent?.onDeselectPoint(this.point);
        }
    }

    handleImageError(event: Event) {
        // Call parent's error handler to mark image as failed and try next image
        super.handleImageError(event);
    }

    render() {
        return html`
            <div class="grid-point-card" @mouseenter=${this.onHoverMarker} @mouseleave=${this.onExitHoverMarker} title=${this.point?.name}>
                ${this.multiSelectionMode ? html`
                        <div class="deselect-control" @click="${this.onDeselectPoint}">
                            <mwc-icon>close</mwc-icon>
                            <span>Deselect</span>
                        </div>`
                    : this.hasImages ?
                            html`<div class="grid-point-card__image-wrapper" @click="${this.onCardClick}">
                                <img @error="${(e: Event) => this.handleImageError(e)}" src=${this.firstImage} class="grid-point-card__image" style="object-fit: ${topicData.topic?.image_display ?? 'cover'}"/>
                            </div>`
                            : this.point ? html`<div class="grid-point-card__image-wrapper" @click="${this.onCardClick}">
                                <img src="${getMarkerIcon(this.point, new Map<string, CheckIn[]>(), [], false, false).url}" class="grid-point-card__image category-icon" />
                            </div>`
                            : ''
                }
                <div class="grid-point-card__content_image" @click="${this.onCardClick}">
                    <span class="grid-point-card__category-wrapper">
                        ${topicData.topic?.enable_point_numbering === true && this.point?.number !== undefined
                            ? html`<img src="${makeColorsIntoNumberSvg(getNumberedPointIconColor(topicData.topic, this.point.topic_category_id.$oid),this.point?.number, getNumberedPointIconShape(topicData.topic, this.point.topic_category_id.$oid))}"
                                        class="grid-point-card__point-number"
                                        width="18"
                                        height="18"/>`
                            : ''
                        }
                        ${shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.title?.hide_on_point_card) ? html`
                            <span class="grid-point-card__title">
                                ${this.point?.featured === true
                                    ? html`
                                        <mwc-icon class="grid-point-card__title--featured">star</mwc-icon>`
                                    : ''
                                }
                                ${isValidString(this.point?.name) ? html`<h2 class="grid-point-card__title-text">${this.point?.featured === true ? `${topicData.topic?.featured_name ?? 'Featured'}:` : ''} ${this.point?.name}</h2>`: ''}
                            </span>
                        ` : ''}
                    </span>
                    ${this.point?.location.place_id !== 'custom' && isFalsy(topicData.topic?.disable_location_ui) && shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.location?.hide_on_point_card)
                        ? html`<span class="mini-point-card__address">
                                ${this.point?.location.search}
                            </span>`
                        : ''
                    }
                </div>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            :host {
                --mdc-typography-button-text-transform: case;
                --mdc-typography-subtitle1-font-size: 12px;
                --mdc-icon-button-size: 36px;
                --mdc-typography-button-letter-spacing: 0px;
                width: min(calc(50% - 5px), 250px);
                box-shadow: 0px 0px 4px 0px var(--theme-border-color);
            }

            .grid-point-card {
                word-wrap: break-word;
                border-radius: 4px;
                display: flex;
                flex-direction: column;
                height: 150px;
                cursor: pointer;
                cursor: pointer;
                font-weight: 400;
            }

            .grid-point-card__content_image {
                padding: 4px 6px 4px 6px;
                overflow: hidden;
                opacity: 0.98;
                flex: 1;
                display: flex;
                align-items: flex-start;
                flex-direction: column;
            }

            .grid-point-card__content_no_image {
                padding: 12px 16px;
                width: 100%;
                background: white;
                overflow: hidden;
                opacity: 0.98;
            }

            .grid-point-card__image {
                width: 100%;
                height: 100%;
                border-radius: 2px;
                object-fit: cover;
                opacity: 1;
            }

            .category-icon {
                object-fit: contain;
                background-color: var(--theme-border-color);
            }

            .grid-point-card__point-number {
                margin-right: 2px;
                z-index: 1;
            }

            .grid-point-card__description {
                color: var(--theme-lighter-text-color);
                display: block;
                margin-bottom: 8px;
                overflow: hidden;
                text-overflow: ellipsis;
                height: 55px;
            }

            .grid-point-card__cover {
                height: 100px;
                background: linear-gradient(to top, white, transparent 100%);
                margin-top: -50px;
                padding: 5px;
                width: 100%;
                margin-left: -5px;
                position: relative;
            }

            .grid-point-card__category-wrapper {
                display: flex;
            }

            .mini-point-card__address {
                color: var(--theme-lighter-text-color);
                display: block;
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
                width: 100%;
            }

            .mini-point-card__event {
                color: var(--theme-lighter-text-color);
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            .mini-point-card__description {
                color: var(--theme-lighter-text-color);
                display: block;
                margin-bottom: 8px;
                overflow: hidden;
                text-overflow: ellipsis;
                height: 55px;
            }

            .mini-point-card__cover {
                height: 40px;
                background: linear-gradient(to top, white, transparent 100%);
                margin-top: -50px;
                padding: 5px;
                width: 100%;
                margin-left: -5px;
                position: relative;
            }


            .grid-point-card__category-pill {
                border-radius: 20px;
                font-size: 12px;
                padding: 5px 8px;
                gap: 5px;
                display: flex;
                align-items: center;
                border: 1px solid var(--theme-border-color);
            }

            .grid-point-card__image-wrapper {
                display: flex;
                overflow: hidden;
                padding: 4px;
                height: 50%;
                align-items: center;
                opacity: 0.98;
                gap: 0.5rem;
                box-shadow: 0px 0px 4px 0px var(--theme-border-color);
            }

            .space-wrapper {
                flex: 1;
                background-color: var(--theme-white-color);
            }

            .grid-point-card__title-text {
                font-size: 14px;
                color: var(--main-primary-color);
                margin: 4px 0 0;
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
                font-weight: 400;
            }

            .grid-point-card__address {
                color: var(--theme-lighter-text-color);
                display: block;
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            .grid-point-card__event {
                color: var(--theme-lighter-text-color);
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            .grid-point-card__event-icon {
                vertical-align: middle;
                --mdc-icon-size: 20px;
                width: 16px;
                padding-right: 4px;
            }

            .grid-point-card__title--featured {
                color: var(--main-featured-color);
                align-self: center;
                padding-right: 4px;
            }

            .grid-point-card__title {
                display: flex;
                width: 100%;
            }

            .grid-point-card:hover {
                background-color: var(--theme-border-color);
            }

            .deselect-control {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 1rem;
                gap: 0.25rem;
                cursor: pointer;
                background-color: var(--theme-opacity-background-color);
                color: var(--main-primary-color);
                --mdc-icon-size: 2rem;
                font-size: 14px;
            }
        `;
    }
}